/* eslint-disable react/no-danger */
import React from 'react';
import { Helmet } from 'react-helmet';
import { BookendsMetaTag, BOOKEND_TYPES } from '@vp/esi-bookends';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import { Provider as ReduxProvider } from 'react-redux';
import { LocalizationProvider } from '@vp/om-translations';
import TrackingJS from '@vp/react-tracking';
import { ScreenClassProvider } from '@vp/swan';
import {
  BookendsHeader,
  BookendsFooter,
} from '../components/bookends/Bookends';
import translationResources from '../../assets/i18n';
import { CartSession } from '../components/CartSession';
import NewRelic from '../components/newrelic/newrelic';
import configureStore from '../state/store';
import { UserFeatureProvider } from '../components/contexts/UserFeatureContext';
import { OptimizelyExperimentsProvider } from '../components/contexts/OptimizelyExperimentsContext';
import { SiteProvider } from '../components/contexts/SiteContext';
import { IdentityProvider } from '../components/contexts/IdentityContext';
import { PageMetadata } from '../components/PageMetadata';
import { TaxDisplayProvider } from '../components/shared/taxDisplay/TaxDisplayContext';
import { TRACKING_LIB_VERSION } from '../constants/tracking';
import { PageModsProvider } from '../components/contexts/PageModsContext';
import { Preconnect } from '../components/preconnect/Preconnect';
import { SwanEnabler } from '../components/shared/SwanEnabler';
import { PromoBar } from '../components/shared/PromoBar';
import { ExpressCheckoutContextProvider } from '../components/contexts/ExpressCheckoutContext';

const cartStateStore = configureStore();

const getBookendType = type =>
  type === 'slim' ? BOOKEND_TYPES.SLIM : BOOKEND_TYPES.FULL;

const CartPage = ({ data }) => {
  const { stcSites: siteData } = data;
  const { vistacartTenantName } = siteData;
  const { locale, language } = siteData.uiLocalization;
  const {
    brand: trackingTenant,
    environment: trackingEnvironment,
    culture: trackingCulture,
  } = siteData.tracking;
  const rawSiteUxMods = siteData.uiData.cart?.uxMods;

  // because gatsby can't pass through objects with 'id' properties, our source json uses 'modId'.
  // we need to map those modRefs into the format that mod-father requires.
  const siteConfigPageUxMods = (rawSiteUxMods || []).map(
    ({ modRef: { family, modId }, scope }) => ({
      modRef: {
        family,
        id: modId,
      },
      scope,
      enabled: true,
    })
  );

  const { bookends } = siteData.uiData.cart;

  const bookendsConfig = {
    ...bookends,
    headerType: getBookendType(bookends?.headerType),
    footerType: getBookendType(bookends?.footerType),
  };

  return (
    <>
      <Preconnect />
      <TrackingJS
        tenant={trackingTenant}
        environment={trackingEnvironment}
        culture={trackingCulture}
        version={TRACKING_LIB_VERSION}
        suppressReloadOnConsentSubmit
      />
      <NewRelic />
      <ScreenClassProvider>
        <div className="site-content om-cart ui-version-3">
          <PageModsProvider siteConfigPageUxMods={siteConfigPageUxMods}>
            <SiteProvider value={siteData}>
              <IdentityProvider>
                <UserFeatureProvider>
                  {/* On page load, you might notice a flash of unstyled content (FOUC). Don't panic, this is a side effect of
                local static site generation that does not occur in prod environments!
                Read more: https://www.gatsbyjs.com/docs/conceptual/overview-of-the-gatsby-build-process/ */}
                  <SwanEnabler>
                    <BookendsMetaTag
                      renderWith={Helmet}
                      locale={bookendsConfig.locale}
                      tenant={bookendsConfig.tenant}
                      headerType={bookendsConfig.headerType}
                      footerType={bookendsConfig.footerType}
                      hideSearch={bookendsConfig.hideSearch}
                      hideVat={bookendsConfig.hideVat}
                    />

                    <BookendsHeader bookendsConfig={bookendsConfig} />
                    <PromoBar />

                    <main className="site-main">
                      {/* Ignoring eslint as this is accepted per UI Library Standards and not an accessibility issue */}
                      {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid */}
                      <a
                        id="maincontentstart"
                        name="maincontentstart"
                        tabIndex="-1"
                      />

                      <LocalizationProvider
                        translationResources={translationResources}
                        locale={locale}
                        logLevel="ERROR"
                      >
                        <OptimizelyExperimentsProvider>
                          <ReduxProvider store={cartStateStore}>
                            <ExpressCheckoutContextProvider>
                              <TaxDisplayProvider tenant={vistacartTenantName}>
                                <PageMetadata language={language} />
                                <CartSession />
                              </TaxDisplayProvider>
                            </ExpressCheckoutContextProvider>
                          </ReduxProvider>
                        </OptimizelyExperimentsProvider>
                      </LocalizationProvider>
                    </main>
                    <BookendsFooter bookendsConfig={bookendsConfig} />
                  </SwanEnabler>
                </UserFeatureProvider>
              </IdentityProvider>
            </SiteProvider>
          </PageModsProvider>
        </div>
      </ScreenClassProvider>
    </>
  );
};

// Data query for site specific context on the cart page
export const query = graphql`
  query ($siteName: String!) {
    stcSites(siteName: { eq: $siteName }) {
      siteName
      country
      siteBaseUrl
      vistacartTenantName
      tenantEnvironment
      tracking {
        brand
        country
        environment
        culture
      }
      uiLocalization {
        language
        locale
      }
      nav {
        myProjects
        privacyPolicy
        termsAndConditions
        orderHistory
        cart
        checkout
        base
        delivery
      }
      uiData {
        addressValidation {
          loqateAPIKey
          loqateEnabledCountries
        }
        addressAutocomplete {
          provider
          apiKey
          enabledCountries
        }
        cart {
          auth {
            allowAnonymous
            site
          }
          bookends {
            tenant
            locale
            headerType
            footerType
            hideSearch
            hideVat
          }
          noCouponEntry
          shippingDisplayOptions {
            shippingPricingInCartType
          }
          uxMods {
            modRef {
              family
              modId
            }
            scope
          }
        }
        checkout {
          auth {
            allowAnonymous
          }
          addressAutocomplete {
            billingAddressEnabled
            shippingAddressEnabled
          }
          addressValidation {
            billingAddressEnabled
            shippingAddressEnabled
          }
        }
      }
      featureControls {
        offerCartSignInGuestCheckoutExperience
        enableSiteWidePromoBar
        showPayPalPayLaterMessaging
        enableFreeShippingOverThresholdProgressBar
      }
      optimizelyExperiments {
        enabledExperimentKeys
      }
      pricingOptions {
        merchantId
      }
      paymentOptions {
        paymentEnvironment
      }
      geoData {
        billing
        shipping
        noPoBoxCountries
      }
    }
  }
`;

CartPage.propTypes = {
  data: PropTypes.object.isRequired,
};

export default CartPage;
